import { useMutation, useQueryClient } from '@tanstack/react-query';
import { evidenceService } from '@services';
import { DRAFT_EVIDENCE_NOT_FOUND_MESSAGE, findDraftEvidence } from '@utils/evidenceVisibility';
import { getApiErrorMessage } from '@utils/apiError';
import { showToast } from '@utils/toast';
import type { DraftEvidenceActionsHandle, Evidence } from '@app-types';

// getApiErrorMessage only reads API error bodies, so the local "no draft" error needs its own branch.
const draftErrorMessage = (error: unknown, fallback: string): string =>
  error instanceof Error && error.message === DRAFT_EVIDENCE_NOT_FOUND_MESSAGE
    ? DRAFT_EVIDENCE_NOT_FOUND_MESSAGE
    : getApiErrorMessage(error, fallback);

/**
 * Submit / delete the Draft Evidence of an assessment item (ADO-1148).
 * Item lists carry only the aggregated status, so the draft row is loaded on
 * demand when an action is taken.
 */
export const useDraftEvidenceActions = (onChanged?: () => void): DraftEvidenceActionsHandle => {
  const queryClient = useQueryClient();

  const loadDraft = async (assessmentItemId: number): Promise<Evidence> => {
    const data = await evidenceService.getEvidenceForItem(assessmentItemId);
    const draft = findDraftEvidence(data.evidence || []);
    if (!draft) {
      throw new Error(DRAFT_EVIDENCE_NOT_FOUND_MESSAGE);
    }
    return draft;
  };

  const refreshAfterChange = (assessmentItemId: number) => {
    void queryClient.invalidateQueries({ queryKey: ['assessment-items'] });
    void queryClient.invalidateQueries({ queryKey: ['contractManagerGapItems'] });
    void queryClient.invalidateQueries({ queryKey: ['assessment-item-evidence', assessmentItemId] });
    void queryClient.invalidateQueries({ queryKey: ['assessorPendingEvidence'] });
    onChanged?.();
  };

  const submitMutation = useMutation({
    mutationFn: async (assessmentItemId: number) => {
      const draft = await loadDraft(assessmentItemId);
      return evidenceService.submitEvidence(draft.id);
    },
    onSuccess: (_result, assessmentItemId) => {
      showToast.success('Evidence submitted for review');
      refreshAfterChange(assessmentItemId);
    },
    onError: (error: unknown) => {
      showToast.error(draftErrorMessage(error, 'Failed to submit evidence'));
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (assessmentItemId: number) => {
      const draft = await loadDraft(assessmentItemId);
      return evidenceService.deleteEvidence(draft.id);
    },
    onSuccess: (_result, assessmentItemId) => {
      showToast.success('Draft evidence deleted');
      refreshAfterChange(assessmentItemId);
    },
    onError: (error: unknown) => {
      showToast.error(draftErrorMessage(error, 'Failed to delete draft evidence'));
    }
  });

  return {
    submitDraft: (assessmentItemId, callbacks) =>
      submitMutation.mutate(assessmentItemId, { onSettled: callbacks?.onSettled }),
    deleteDraft: (assessmentItemId, callbacks) =>
      deleteMutation.mutate(assessmentItemId, { onSettled: callbacks?.onSettled }),
    isSubmitting: submitMutation.isPending,
    isDeleting: deleteMutation.isPending
  };
};
